/**
 * practiceStats · чистая деривация всех чисел хаба «Твоя практика»
 * (мокап 04_DESIGN/mockup_practice_hub_today.html · спек
 * COORDINATION/specs/practice_hub_stats_logic.md · решения Genie 24 JUN 2026).
 *
 * Источники — только то, что уже хранится: HOMEWORK_V2 (статика) + progress.homework
 * (status / startedAt / completedAt / selfCheck / submission уже пишутся
 * `setHomeworkStatus`). Бэкенд и схема не трогаются.
 *
 * Активность (кольцо дня · хитмап · стрик) считается ПО ВРЕМЕНИ: `activityMinByDay`
 * (минуты на экране + Pomodoro) инжектится вызывающим (см. `pomodoroMinutesByDay`).
 * Радар — 9 осей; ось берётся `hw.skillAxis` (оверрайд) или выводится `deriveSkillAxis`.
 *
 * Чистая функция · без React · `now` инжектится для тестируемости.
 */
import type { HomeworkV2, SkillAxis } from '@/data/homework-v2';
import type { HomeworkProgress } from '@/types';

// ─── оси радара (9 · весь курс) ───────────────────────────────────────────────
export const SKILL_AXIS_ORDER: ReadonlyArray<SkillAxis> = [
  'requirements',
  'test-design',
  'bug-report',
  'data-sql',
  'api',
  'non-functional',
  'automation',
  'ai',
  'exploratory',
];

export const SKILL_AXIS_LABEL: Record<SkillAxis, string> = {
  requirements: 'Требования',
  'test-design': 'Тест-дизайн',
  'bug-report': 'Дефекты',
  'data-sql': 'SQL · Данные',
  api: 'API',
  'non-functional': 'Нефункциональное',
  automation: 'Автоматизация',
  ai: 'AI',
  exploratory: 'Исследование',
};

/**
 * Дефолт-деривация оси по atelier/title/sprint/surface.
 * Используется, если у домашки не задан `skillAxis`-оверрайд.
 * Покрывает весь курс (S01-S18), чтобы будущие домашки S11+ маппились корректно.
 */
export function deriveSkillAxis(hw: HomeworkV2): SkillAxis {
  const t = (hw.title ?? '').toLowerCase();
  const screen = hw.atelier?.screen;
  // 1. явные practice-mode экраны Ательера
  if (screen === 'sql-playground') return 'data-sql';
  if (screen === 'api-explorer') return 'api';
  if (screen === 'pom-explorer' || screen === 'ci-pipeline') return 'automation';
  // 2. сильные сигналы по названию
  if (/\bai\b|искусствен|llm|gpt|промпт|prompt|нейросет/.test(t)) return 'ai';
  if (/перформанс|performance|нагруз|производительн|безопасн|security|уязвим|нефункц/.test(t)) return 'non-functional';
  if (/автоматиз|selenium|playwright|pytest|\bpom\b|page object|локатор|locator|\bci\b|pipeline|cypress/.test(t)) return 'automation';
  if (/охот|исследов|чартер|разведк|exploratory/.test(t)) return 'exploratory';
  if (hw.surface?.kind === 'bugReport' || /баг[\s-]?репорт|дефект|\bрепорт/.test(t)) return 'bug-report';
  if (/nosql|mongo|redis|\bsql\b|запрос|база данных|таблиц/.test(t)) return 'data-sql';
  if (/\bapi\b|endpoint|http|postman|soapui|\brest\b/.test(t)) return 'api';
  // 3. по спринту
  const sp = (hw.sprintId ?? '').toUpperCase();
  const num = parseInt(sp.replace(/\D/g, ''), 10) || 0;
  if (sp === 'S02') return 'requirements';
  if (sp === 'S03' || sp === 'S04' || sp === 'S05' || sp === 'S06') return 'test-design';
  if (sp === 'S07') return 'bug-report';
  if (sp === 'S08' || sp === 'S11') return 'data-sql';
  if (sp === 'S09' || sp === 'S10') return 'api';
  if (sp === 'S12') return 'non-functional';
  if (sp === 'S13') return 'exploratory'; // мобильное «попробовать руками»
  if (num >= 14 && num <= 17) return 'automation';
  if (num >= 18) return 'ai';
  // 4. по поверхности
  if (hw.surface?.kind === 'storyForm' || hw.surface?.kind === 'acList' || hw.surface?.kind === 'moscowBoard')
    return 'requirements';
  return 'requirements';
}

export const axisOf = (hw: HomeworkV2): SkillAxis => hw.skillAxis ?? deriveSkillAxis(hw);

// ─── date helpers (локальная дата) ───────────────────────────────────────────
export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function daysBetween(a: Date, b: Date): number {
  const sa = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const sb = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((sa - sb) / 86400000);
}
/** «сегодня» / «вчера» / «N дн.» из ISO-времени сдачи. */
export function relativeDayLabel(iso: string, now: Date): string {
  const diff = daysBetween(now, new Date(iso));
  if (diff <= 0) return 'сегодня';
  if (diff === 1) return 'вчера';
  return `${diff} дн.`;
}

// ─── активность по времени ───────────────────────────────────────────────────
/**
 * Минуты Pomodoro по дням из `progress.achievements.pomodoroSessions[]`
 * (каждая завершённая рабочая сессия = `workMin`, дефолт 25). `at` — Date.now ms.
 */
export function pomodoroMinutesByDay(
  sessions: ReadonlyArray<{ at: number }>,
  workMin = 25,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const s of sessions) {
    const k = dayKey(new Date(s.at));
    out[k] = (out[k] ?? 0) + workMin;
  }
  return out;
}

/** Уровень интенсивности дня по минутам активности (для хитмапа). */
function activityLevel(min: number): 0 | 1 | 2 | 3 | 4 {
  if (min <= 0) return 0;
  if (min < 15) return 1;
  if (min < 30) return 2;
  if (min < 60) return 3;
  return 4;
}

// ─── типы выхода ─────────────────────────────────────────────────────────────
export interface RadarAxis { axis: SkillAxis; label: string; done: number; total: number; value: number }
export interface HeatCell { dateKey: string; minutes: number; level: 0 | 1 | 2 | 3 | 4 }
export interface FeedItem { id: string; title: string; completedAt: string; label: string }
export interface SprintProgressRow { sprintId: string; label: string; done: number; total: number }
export interface NextStep { hw: HomeworkV2; selfCheckDone: number; selfCheckTotal: number; resume: boolean }
export interface PracticeKpi {
  done: number; total: number; inProgress: number;
  minutesDone: number; minutesTotal: number; pct: number; artifacts: number;
}
export interface PracticeStats {
  kpi: PracticeKpi;
  next: NextStep | null;
  /** Кольцо дня · по ЭКРАННОМУ времени активности (минуты). */
  daily: { goalMin: number; activeMin: number; pct: number };
  /** Pomodoro отдельно · «время в фокусе» (минуты сегодня / на неделе). */
  focus: { todayMin: number; weekMin: number };
  streak: number;
  radar: RadarAxis[];
  weakest: RadarAxis | null;
  strongest: RadarAxis | null;
  heatmap: HeatCell[];
  week: { done: number; goal: number };
  feed: FeedItem[];
  perSprint: SprintProgressRow[];
}

export interface PracticeStatsInput {
  /** домашки ОТКРЫТЫХ спринтов (фильтр по доступу делает вызывающий) */
  available: ReadonlyArray<HomeworkV2>;
  progressHw: Record<string, HomeworkProgress | undefined>;
  sprints: ReadonlyArray<{ id: string; ord: number; title: string }>;
  activeSprintId: string;
  /** минуты ЭКРАННОЙ активности по дням; пусто, пока трекер не пишет данные */
  activityMinByDay?: Record<string, number>;
  /** минуты Pomodoro по дням («время в фокусе») · отдельно от активности */
  pomodoroMinByDay?: Record<string, number>;
  now?: Date;
  dailyGoalMin?: number;
  weeklyGoal?: number;
  heatmapWeeks?: number;
}

const DEFAULTS = { dailyGoalMin: 30, weeklyGoal: 5, heatmapWeeks: 14 };

function sprintHead(title: string): string {
  return title.split(/[—·]/)[0]?.trim() ?? title;
}

export function computePracticeStats(input: PracticeStatsInput): PracticeStats {
  const now = input.now ?? new Date();
  const goalMin = input.dailyGoalMin ?? DEFAULTS.dailyGoalMin;
  const weeklyGoal = input.weeklyGoal ?? DEFAULTS.weeklyGoal;
  const heatWeeks = input.heatmapWeeks ?? DEFAULTS.heatmapWeeks;
  const activity = input.activityMinByDay ?? {};
  const { available, progressHw } = input;

  const prog = (id: string) => progressHw[id];
  const isDone = (hw: HomeworkV2) => prog(hw.id)?.status === 'done';
  const isInProgress = (hw: HomeworkV2) => prog(hw.id)?.status === 'in-progress';
  const doneHw = available.filter(isDone);

  // ── KPI ──
  const minutesDone = doneHw.reduce((s, h) => s + (h.estimateMin || 0), 0);
  const minutesTotal = available.reduce((s, h) => s + (h.estimateMin || 0), 0);
  const kpi: PracticeKpi = {
    done: doneHw.length,
    total: available.length,
    inProgress: available.filter(isInProgress).length,
    minutesDone,
    minutesTotal,
    pct: available.length ? Math.round((doneHw.length / available.length) * 100) : 0,
    artifacts: doneHw.filter((h) => Boolean(prog(h.id)?.submission)).length,
  };

  // ── Сегодня · твой ход ──
  const inProgList = available
    .filter(isInProgress)
    .sort((a, b) => (prog(b.id)?.startedAt ?? '').localeCompare(prog(a.id)?.startedAt ?? ''));
  let nextHw: HomeworkV2 | undefined = inProgList[0];
  let resume = true;
  if (!nextHw) {
    nextHw = available.find(
      (h) => h.sprintId === input.activeSprintId && prog(h.id)?.status !== 'done' && !isInProgress(h),
    );
    resume = false;
  }
  let next: NextStep | null = null;
  if (nextHw) {
    const sc = prog(nextHw.id)?.selfCheck ?? {};
    next = {
      hw: nextHw,
      selfCheckDone: Object.values(sc).filter(Boolean).length,
      selfCheckTotal: nextHw.selfCheck.length,
      resume,
    };
  }

  // ── Дневная цель (время активности) ──
  const todayKey = dayKey(now);
  const activeMinToday = activity[todayKey] ?? 0;
  const daily = {
    goalMin,
    activeMin: activeMinToday,
    pct: goalMin > 0 ? Math.min(100, Math.round((activeMinToday / goalMin) * 100)) : 0,
  };

  // ── Стрик (дни с активностью) ──
  let streak = 0;
  let cursor = now;
  const hasActivity = (d: Date) => (activity[dayKey(d)] ?? 0) > 0;
  if (!hasActivity(cursor)) cursor = addDays(cursor, -1);
  while (hasActivity(cursor)) {
    streak += 1;
    cursor = addDays(cursor, -1);
  }

  // ── Радар (9 осей) ──
  const tally = new Map<SkillAxis, { done: number; total: number }>();
  for (const a of SKILL_AXIS_ORDER) tally.set(a, { done: 0, total: 0 });
  for (const h of available) {
    const cell = tally.get(axisOf(h))!;
    cell.total += 1;
    if (isDone(h)) cell.done += 1;
  }
  const radar: RadarAxis[] = SKILL_AXIS_ORDER.map((a) => {
    const { done, total } = tally.get(a)!;
    return { axis: a, label: SKILL_AXIS_LABEL[a], done, total, value: total ? done / total : 0 };
  });
  const withTotal = radar.filter((r) => r.total > 0);
  const weakest = withTotal.length ? withTotal.reduce((m, r) => (r.value < m.value ? r : m)) : null;
  const strongest = withTotal.length ? withTotal.reduce((m, r) => (r.value > m.value ? r : m)) : null;

  // ── Активность · хитмап (минуты по дням) ──
  const heatmap: HeatCell[] = [];
  const totalDays = heatWeeks * 7;
  for (let i = totalDays - 1; i >= 0; i--) {
    const k = dayKey(addDays(now, -i));
    const minutes = activity[k] ?? 0;
    heatmap.push({ dateKey: k, minutes, level: activityLevel(minutes) });
  }

  // ── Цель недели (сдано заданий · ISO-неделя, понедельник) ──
  const offsetToMonday = (now.getDay() + 6) % 7;
  const monday = addDays(now, -offsetToMonday);
  const weekDone = doneHw.filter((h) => {
    const c = prog(h.id)?.completedAt;
    return c ? new Date(c).getTime() >= monday.getTime() : false;
  }).length;
  const week = { done: weekDone, goal: weeklyGoal };

  // ── Pomodoro · «время в фокусе» (отдельно от активности) ──
  const pomo = input.pomodoroMinByDay ?? {};
  let focusWeek = 0;
  for (const [k, v] of Object.entries(pomo)) {
    const [yy, mm, dd] = k.split('-').map(Number) as [number, number, number];
    if (new Date(yy, mm - 1, dd).getTime() >= monday.getTime()) focusWeek += v;
  }
  const focus = { todayMin: pomo[todayKey] ?? 0, weekMin: focusWeek };

  // ── Лента «недавно сдано» ──
  const feed: FeedItem[] = doneHw
    .map((h) => ({ hw: h, c: prog(h.id)?.completedAt }))
    .filter((x): x is { hw: HomeworkV2; c: string } => Boolean(x.c))
    .sort((a, b) => b.c.localeCompare(a.c))
    .slice(0, 5)
    .map(({ hw, c }) => ({ id: hw.id, title: hw.title, completedAt: c, label: relativeDayLabel(c, now) }));

  // ── Прогресс по спринтам (для списка) ──
  const perSprint: SprintProgressRow[] = [];
  for (const sp of [...input.sprints].sort((a, b) => a.ord - b.ord)) {
    const rows = available.filter((h) => h.sprintId === sp.id);
    if (rows.length === 0) continue;
    perSprint.push({
      sprintId: sp.id,
      label: `Спринт ${String(sp.ord).padStart(2, '0')} · ${sprintHead(sp.title)}`,
      done: rows.filter(isDone).length,
      total: rows.length,
    });
  }

  return { kpi, next, daily, focus, streak, radar, weakest, strongest, heatmap, week, feed, perSprint };
}
